import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import captchaStyled from "components/captcha/CaptchaStyle";
import LanguageContext from "components/LanguageContext";
import PropTypes from "prop-types";
import React, { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import t from "utils/locales/translation.json";

const defaultCaptchaOptions = {
  principalCaptcha: "QUESTION",
  alternativeCaptcha: "AUDIO",
};

const Captcha = forwardRef((props, ref) => {
  const { id, onCaptchaValidate, onCaptchaLoaded } = props;

  const [captchaErrorMessage, setCaptchaErrorMessage] = useState("");
  const captchaContainerRef = useRef(null);

  const { language } = useContext(LanguageContext);
  const [oldLanguage, setOldLanguage] = useState(language);
  const [captchaIsLoaded, setCaptchaIsLoaded] = useState(false);

  // on first send form, this useffect has run 2 times, the first time to load the script, the second time to execute it.
  useEffect(() => {
    // add liveidentity script
    const liveidentityScript = document.querySelector("#liveidentityScript");
    if (process.env.liveidentity_URL && process.env.liveidentity_key && typeof document !== "undefined") {
      if (liveidentityScript) {
        // no need to reload script
        if (!captchaIsLoaded) {
          window.LI_ANTIBOT.reload();
          onCaptchaLoaded();
        }
        window.LI_ANTIBOT.ready(() => {
          window.LI_ANTIBOT.execute(defaultCaptchaOptions).then(() => {
            onCaptchaValidate();
            setCaptchaIsLoaded(false);
          });
        });
      } else {
        const scriptTag = document.createElement("script");
        scriptTag.src = `${process.env.liveidentity_URL}/captcha/public/antibot.min.v5.1.js`;
        scriptTag.async = true;
        scriptTag.id = "liveidentityScript";
        scriptTag.setAttribute("data-spKey", process.env.liveidentity_key);
        scriptTag.onload = () => {
          setCaptchaIsLoaded(true);
          onCaptchaLoaded();
        };
        document.body.appendChild(scriptTag);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCaptchaLoaded, onCaptchaValidate]);

  useEffect(() => {
    // trigger on language changes
    if (language !== oldLanguage) {
      setCaptchaErrorMessage("");
      setOldLanguage(language);
      window.LI_ANTIBOT.reload();
      window.LI_ANTIBOT.execute({
        ...defaultCaptchaOptions,
        locale: language,
      }).then(onCaptchaValidate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    const inputAntibot = document.querySelector("#li-antibot-token");
    if (captchaErrorMessage && inputAntibot) {
      const observer = new MutationObserver((_, obs) => {
        setCaptchaErrorMessage("");
        obs.disconnect();
      });
      observer.observe(inputAntibot, { attributes: true });
    }
  }, [captchaErrorMessage]);

  const getToken = () => {
    const token = document.getElementById("li-antibot-token")?.value;
    return token === "Invalid response." || token === "" ? null : token;
  };

  const getTokenCode = () => {
    const tokenCode = document.getElementById("li-antibot-token-code")?.value;
    return tokenCode;
  };

  const verifyCaptchaLoaded = () => {
    // if li-antibot-token is here, the script loaded
    return document.getElementById("li-antibot-token") !== null;
  };

  // exposed data to ref
  useImperativeHandle(
    ref,
    () => ({
      verifyCaptchaLoaded,
      getToken,
      getTokenCode,
      setCaptchaErrorMessage,
    }),
    [],
  );

  if (!process.env.liveidentity_key) return null;

  return (
    <div key={id} className="pre-captcha-wrapper">
      <div aria-label={t[language].components.captcha_message} />
      <Box
        className="captcha-wrapper"
        sx={{
          width: "100%",
          mt: 1.5,
          border: captchaErrorMessage === "" ? "none" : "1px solid #f44336",
          "& .status": {
            color: "#f44336",
          },
          "& .img > a": {
            display: "inline-block",
          },
          ...captchaStyled,
        }}
      >
        <div id="li-antibot" ref={captchaContainerRef} />
      </Box>
      {captchaErrorMessage && <FormHelperText className="status">{captchaErrorMessage}</FormHelperText>}
    </div>
  );
});

Captcha.propTypes = {
  id: PropTypes.string.isRequired,
  onCaptchaValidate: PropTypes.func.isRequired,
  onCaptchaLoaded: PropTypes.func.isRequired,
};

export default Captcha;
